import type { Dict } from './en';

export const fr: Dict = {
  subtitle: 'Devinez le pays du jour',
  modes: { classic: 'Classique', flag: 'Drapeau', outline: 'Silhouette', connections: 'Connexions', trivia: 'Quiz', draw: 'Dessin' },
  recommendations: {
    title: 'Vous aimerez peut-être aussi',
    open: 'Ouvrir {name}',
    games: {
      wormillion: 'Jeu quotidien de géographie aux réponses rares',
      stardewdle: 'Puzzles quotidiens dans l’univers de Stardew Valley',
      cascadle: 'Jeu de mots quotidien en cascade',
      farhike: 'Défi quotidien sur la carte avec des pays peu cités',
    },
  },
  draw: {
    subtitle: 'Dessinez la silhouette de mémoire',
    askLabel: 'Dessinez ce pays',
    drawingLabel: 'En train de dessiner',
    askHint: 'De mémoire — sans référence, un seul essai.',
    blank: 'une feuille blanche — pas de calque',
    canvasLabel: 'Zone de dessin',
    start: 'Commencer à dessiner',
    undo: 'Annuler',
    clear: 'Effacer',
    done: 'Terminé',
    next: 'Pays suivant',
    score: 'Score',
    accuracy: 'Précision',
    coverage: 'Couverture',
    deviation: 'Écart',
    legendTrue: 'Silhouette réelle',
    legendYours: 'Votre tracé',
    legendOut: 'Trop à l’extérieur',
    legendIn: 'Trop à l’intérieur',
    pts: 'pts',
    learnMore: 'En savoir plus',
    ranks: [
      'Cartographe royal',
      'Maître cartographe',
      'Bon géomètre',
      'Main sûre',
      'Apprenti',
      'Croquis grossier',
      'Retour à l’atlas',
    ],
  },
  trivia: {
    subtitle: 'Dix questions de géographie par jour',
    loading: 'Chargement des questions du jour…',
    question: 'Question',
    correct: 'Correct !',
    wrong: 'Presque',
    next: 'Question suivante',
    seeResult: 'Voir le résultat',
    xpNote: '3 XP par bonne réponse, +10 pour un sans-faute.',
    comeBack: 'Dix nouvelles questions à minuit UTC.',
    tiers: ['Sans faute — 10 sur 10 !', 'Bien joué.', 'Pas mal. À demain.', 'Journée difficile. Le monde est vaste.'],
  },
  conn: {
    subtitle: 'Regroupez 16 pays en 4 groupes',
    oneAway: 'Presque !',
    shuffle: 'Mélanger',
    deselect: 'Désélectionner',
    submit: 'Valider',
    mistakes: 'Erreurs',
    solved: 'Résolu !',
    lost: 'La prochaine fois !',
    nextPuzzle: 'Suivant',
    flawless: 'Parfait, aucune erreur !',
    wellPlayed: 'Bien trié !',
    lostSub: 'Les quatre groupes sont révélés ci-dessus.',
    guesses: 'Essais',
  },
  soon: 'bientôt',
  streak: 'Série',
  today: 'Aujourd’hui',
  yesterday: 'Hier',
  nextIn: 'Prochain pays dans',
  daily: 'Quotidien',
  unlimited: 'Unlimited',
  nextCountry: 'Pays suivant',
  skip: 'Passer',
  solved: 'Résolus',
  best: 'Record',
  emptyHint: 'Faites une première proposition pour révéler les indices.',
  boardScrollLabel: 'Tableau comparatif des essais. Faites défiler horizontalement pour voir tous les indices.',
  legend: { correct: 'Correspond', incorrect: 'Aucune', numbers: 'réponse ↑ plus · ↓ moins', distance: 'plus proche → plus loin' },
  placeholder: 'Tapez un pays…',
  guess: 'Deviner',
  won: 'Résolu !',
  lost: 'Plus d’essais',
  answerWas: 'Le pays était',
  answerCard: { viewProfile: 'Voir la fiche', northernHemisphere: 'Hémisphère Nord', southernHemisphere: 'Hémisphère Sud' },
  share: 'Partager',
  copyLink: 'Copier le lien',
  copyResult: 'Copier le résultat',
  copyEmbed: 'Copier l’intégration',
  saveImage: 'Enregistrer l’image',
  shareToday: 'Partager le jour',
  copied: 'Copié !',
  cols: { region: 'Région', pop: 'Pop.', area: 'Superficie', borders: 'Voisins', gdp: 'PIB', temp: 'Temp.', dist: 'Distance' },
  colTips: {
    region: "Sur quel continent se trouve le pays. Vert = même continent que la réponse.",
    dist: "Distance à vol d’oiseau entre votre essai et la réponse. Rouge = loin, vert = proche ; la flèche pointe vers la réponse.",
    borders: "Nombre de pays avec lesquels il partage une frontière terrestre. ↑ = la réponse en a plus.",
    temp: "Température moyenne annuelle, 1991–2020, moyennée sur tout le pays et non sur la capitale.",
    pop: "Population totale. ↑ = la réponse est plus peuplée.",
    area: "Superficie en km². ↑ = la réponse est plus vaste.",
    gdp: "PIB par habitant en dollars. ↑ = la réponse est plus riche par habitant.",
  },
  puzzle: {
    flag: 'À quel pays appartient ce drapeau ?',
    outline: 'Quel pays a cette silhouette ?',
  },
  account: {
    signIn: 'Se connecter',
    withGoogle: 'Continuer avec Google',
    withDiscord: 'Continuer avec Discord',
    tagline: 'Sauvegardez votre série sur tous vos appareils',
    signOut: 'Se déconnecter',
    level: 'Niv',
    viewProfile: 'Voir le profil',
    editProfile: 'Modifier le profil',
  },
  settings: {
    title: 'Modifier le profil',
    avatar: 'Avatar',
    change: 'Changer',
    displayName: 'Nom affiché',
    bio: 'Bio',
    bioPh: 'Parlez de vous aux explorateurs…',
    birthday: 'Anniversaire',
    country: 'Pays',
    countryNone: 'Non défini',
    gender: 'Genre',
    genders: { male: 'Homme', female: 'Femme', other: 'Autre', na: 'Je préfère ne pas dire' },
    socials: 'Réseaux sociaux',
    invalidUrl: 'Entrez une URL valide pour cette plateforme.',
    date: { year: 'Année', month: 'Mois', day: 'Jour' },
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    saved: 'Enregistré',
    signInRequired: 'Connectez-vous pour modifier votre profil.',
    cropTitle: "Recadrer l'avatar",
    cropHint: 'Glissez pour déplacer · molette pour zoomer',
    apply: 'Appliquer',
    cancel: 'Annuler',
    pick: 'Choisir une image',
    uploadError: "Échec de l'envoi — essayez une image plus petite.",
  },
  netError: 'Problème réseau, réessayez.',
  offlinePractice: 'Entraînement hors ligne — non classé.',
  xp: 'XP',
  levelUp: 'Niveau supérieur !',
  honors: {
    title: 'Insignes et médailles',
    intro: 'Chaque énigme résolue est consignée de trois façons : un rang que vous portez, une médaille que vous gagnez, un insigne que vous défendez.',
    levelT: 'Insignes de niveau',
    levelD: 'L’XP des parties quotidiennes vous fait gravir 14 vaisseaux gravés du voyage, du bâton de marcheur au vaisseau amiral qui fait le tour du monde.',
    dailyT: 'Médailles du jour',
    dailyD: 'Le classement du jour range les explorateurs selon le nombre de modes terminés parmi les six, puis selon le nombre d’essais. Les trois premiers emportent l’or, l’argent et le bronze — comptabilisés à jamais sur votre profil.',
    boardT: 'Insignes de classement',
    boardD: 'Tenez une place sur le podium des classements Série, Illimité ou Niveau et chaque journée est gravée sur l’insigne. Les jours d’or, d’argent et de bronze sont comptés séparément.',
    cta: 'Voir le classement',
  },
  lb: {
    scoreBreakdown: "Détail du score",
    scoreAvg: "Moyenne",
    scoreNote: "Classement d’abord par modes terminés, puis par cette moyenne.",
    title: 'Classement',
    today: "Aujourd'hui",
    streak: 'Série',
    level: 'Niveau',
    unlimited: 'Illimité',
    connections: 'Connexions',
    myRank: 'Votre rang',
    empty: 'Aucune entrée — soyez le premier.',
    signInToRank: 'Connectez-vous pour apparaître au classement.',
    guesses: 'essais',
    guess1: 'essai',
    totalSolved: 'Résolus',
    yourStats: 'Vos statistiques',
    back: 'Retour au jeu',
    nav: 'Classement',
    viewAll: 'Voir tout',
    dailyMedals: 'Médailles du jour',
    heldBadges: 'Insignes de classement',
    notFound: 'Explorateur introuvable.',
  },
  privacy: {
    title: 'Politique de Confidentialité',
    updated: 'Dernière mise à jour : 28 juin 2026',
    sections: [
      {
        h: 'Qui sommes-nous',
        p: ['GeoDle (« nous ») est un jeu quotidien et gratuit consistant à deviner des pays, disponible sur geodle.org.'],
      },
      {
        h: 'Ce que nous collectons',
        p: [
          'Données de jeu : vos essais, votre progression quotidienne, votre série et vos points d’expérience — pour faire fonctionner le jeu et le synchroniser entre vos appareils.',
          'Données de compte (uniquement si vous vous connectez avec Google ou Discord) : un identifiant unique, votre nom affiché et votre avatar. Nous ne recevons jamais votre mot de passe.',
        ],
      },
      {
        h: 'Comment nous utilisons vos données',
        p: [
          'Pour enregistrer et synchroniser votre progression, calculer les séries et les niveaux, et afficher les classements et profils publics des joueurs connectés.',
          'Nous ne vendons pas vos données et ne les utilisons pas à des fins publicitaires.',
        ],
      },
      {
        h: 'Analytique et cookies',
        p: [
          'Nous utilisons Google Analytics et Microsoft Clarity pour comprendre l’usage anonyme et agrégé. Ces services peuvent déposer leurs propres cookies ; nous ne les utilisons pas pour vous identifier personnellement.',
          'Nous utilisons un cookie propriétaire pour vous garder connecté et synchroniser votre progression.',
        ],
      },
      {
        h: 'Vos droits et la suppression',
        p: [
          'Vous pouvez supprimer définitivement votre compte et toutes les données associées à tout moment via le bouton ci-dessous. La suppression est immédiate.',
        ],
      },
      { h: 'Contact', p: ['Des questions sur vos données ? Écrivez à contact@geodle.org.'] },
    ],
    deleteAccount: 'Supprimer mon compte et mes données',
    deleteConfirm: 'Ceci supprime définitivement votre compte et toute progression. Continuer ?',
    deleted: 'Votre compte et vos données ont été supprimés.',
  },
  terms: {
    title: 'Conditions d’Utilisation',
    updated: 'Dernière mise à jour : 28 juin 2026',
    sections: [
      {
        h: 'Acceptation',
        p: ['En utilisant GeoDle, vous acceptez ces conditions. Si vous n’êtes pas d’accord, n’utilisez pas le service.'],
      },
      {
        h: 'Le service',
        p: [
          'GeoDle est un jeu quotidien et gratuit fourni « tel quel », pour un divertissement personnel et non commercial. Nous pouvons modifier, suspendre ou interrompre toute partie à tout moment.',
        ],
      },
      {
        h: 'Comptes et fair-play',
        p: [
          'La connexion est facultative et sert à synchroniser votre progression et à vous classer. Vous êtes responsable de l’activité de votre compte.',
          'Ne trichez pas, n’automatisez pas le jeu et ne manipulez pas les scores, séries ou classements. Nous pouvons supprimer les entrées ou comptes concernés.',
        ],
      },
      {
        h: 'Usage acceptable',
        p: [
          'Choisissez un nom et un avatar qui ne sont ni offensants, ni contrefaisants, ni trompeurs. Nous pouvons supprimer tout contenu ou compte enfreignant cette règle.',
        ],
      },
      {
        h: 'Propriété intellectuelle',
        p: [
          'Les données pays proviennent de jeux de données ouverts (mledoze/countries, Natural Earth). GeoDle est un projet indépendant, sans affiliation à un gouvernement ou une organisation.',
        ],
      },
      {
        h: 'Avertissement et responsabilité',
        p: [
          'Le service est fourni sans garantie d’aucune sorte. Dans la mesure permise par la loi, nous ne sommes pas responsables des dommages résultant de votre utilisation de GeoDle.',
        ],
      },
      {
        h: 'Modifications',
        p: ['Nous pouvons mettre à jour ces conditions de temps à autre. Continuer à utiliser le service après une modification vaut acceptation des nouvelles conditions.'],
      },
      { h: 'Contact', p: ['Des questions ? Écrivez à contact@geodle.org.'] },
    ],
  },
  country: {
    whereOnEarth: 'Où sur Terre',
    atAGlance: 'En bref',
    howItCompares: 'Comparaison',
    neighbours: 'Pays voisins',
    inGeoDle: '{name} dans GeoDle',
    exploreNearby: 'À proximité',
    faqTitle: 'FAQ',
    metaTitle: '{name} — Capitale, population, superficie et infos | GeoDle',
    metaDesc: '{name} : capitale {capital}, population {pop}, superficie {area} km². {rankPop} pays le plus peuplé, en {region}. Infos, carte, voisins et plus.',
    getToKnow: 'Découvrir {name}',
    k: { history: 'Moments historiques', people: 'Personnalités célèbres', landmarks: 'Sites incontournables', culture: 'Culture et saveurs' },
    sources: 'Sources',
    listTitle: 'Pays',
    listSearch: 'Rechercher un pays…',
    listAll: 'Toutes les régions',
    listCount: '{n} pays',
    listEmpty: 'Aucun pays ne correspond à votre recherche.',
    promoTitle: 'Explorez tous les pays',
    promoDesc: 'Drapeaux, capitales, populations et un globe interactif : une page complète pour les 194 pays.',
    promoCta: 'Parcourir les pays',
    metaTitleList: 'Tous les pays du monde — drapeaux, capitales et infos | GeoDle',
    metaDescList: 'Parcourez tous les pays : drapeaux, capitales, population, superficie et plus. Recherchez et filtrez par région, et explorez le monde sur un globe interactif.',
    play: 'Jouer',
    guessFlag: 'Devinez le drapeau',
    guessOutline: 'Devinez la silhouette',
    back: 'GeoDle',
    notFound: 'Pays introuvable.',
    landlocked: 'Sans littoral',
    coastal: 'Côtier',
    north: 'Nord',
    south: 'Sud',
    landBorders: '{n} frontières terrestres',
    landBorder: '1 frontière terrestre',
    noBorders: '{name} n’a aucune frontière terrestre.',
    locLede: '{name} se situe en {subregion}, {region}.',
    locLandlocked: 'Le pays est enclavé, sans accès à la mer.',
    locCoastal: 'Le pays possède une façade maritime.',
    locBorders: 'Il partage des frontières terrestres avec {n} pays.',
    locBorder: 'Il partage une frontière terrestre avec un pays.',
    locIsland: 'En tant que pays insulaire, il n’a pas de frontière terrestre.',
    f: { capital: 'Capitale', population: 'Population', area: 'Superficie', region: 'Région', subregion: 'Sous-région', coast: 'Côte', hemisphere: 'Hémisphère', iso: 'ISO', languages: 'Langues', currency: 'Monnaie', timezone: 'Fuseau horaire', calling: 'Indicatif', driving: 'Conduite', tld: 'Domaine', nativeName: 'Nom local' },
    s: {
      popWorld: 'pays le plus peuplé au monde (sur {total}).',
      popRegion: 'plus peuplé de {region} (sur {total}).',
      areaLargest: 'plus grand par superficie',
      biggerThan: 'plus grand que {x}',
      smallerThan: 'plus petit que {x}',
      perKm2: 'habitants par km²',
      sparse: 'peu peuplé',
      moderate: 'densité moyenne',
      dense: 'très peuplé',
      morePeople: 'plus peuplé que {x}',
      fewerPeople: 'moins que {x}',
      vs: 'vs',
      neighbours: 'voisins terrestres',
      neighbour: 'voisin terrestre',
    },
    q: {
      capitalQ: 'Quelle est la capitale de {name} ?',
      capitalA: 'La capitale de {name} est {capital}.',
      popQ: 'Quelle est la population de {name} ?',
      popA: '{name} compte environ {pop} habitants — le {rank} pays le plus peuplé au monde.',
      areaQ: 'Quelle est la superficie de {name} ?',
      areaA: '{name} couvre {area} km², ce qui en fait le {rank} pays le plus vaste.',
      regionQ: 'Dans quelle région se trouve {name} ?',
      regionA: '{name} se trouve en {subregion}, {region}.',
    },
  },
  footer:
    'Données des pays : mledoze/countries (ODbL) · Données cartographiques : Natural Earth (domaine public). GeoDle est un projet indépendant, sans affiliation à un gouvernement ou une organisation.',
};
